import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AppLanguage } from '../global';

/**
 * Arayüz dili (Ayarlar > Genel > Dil): Türkçe ya da İngilizce.
 *
 * Yalnızca iki dil var, bu yüzden metinler ayrı bir sözlükte değil, kullanıldığı
 * yerde iki dilde birlikte yazılıyor: `t('Kaydet', 'Save')`. Böylece bir çeviri
 * hiçbir zaman eksik kalamaz ve değişkenli metinler düz şablon dizesiyle yazılır.
 *
 * Kalıcı değer ana süreçte (settings.json); ana süreç kendi ürettiği mesajları
 * da bu dile göre seçiyor. localStorage'daki kopya yalnızca ilk çizimin doğru
 * dilde olması için: IPC yanıtı gelene kadar arayüz Türkçe yanıp sönmesin.
 */

const STORAGE_KEY = 'zabit.language';

function readCachedLanguage(): AppLanguage {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'en' ? 'en' : 'tr';
  } catch {
    return 'tr';
  }
}

function writeCachedLanguage(language: AppLanguage): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Depolama kullanılamıyorsa kalıcı değer yine ana süreçte.
  }
}

let current: AppLanguage = readCachedLanguage();
// Testler bu modülü Node'da da yüklüyor; orada document yok.
if (typeof document !== 'undefined') document.documentElement.lang = current;

/**
 * Geçerli dildeki metni seçer. React dışındaki yardımcılar da kullanabilir;
 * bileşenler dil değişince yeniden çizilsin diye `useLanguage` çağırmalı
 * (App çağırıyor, altındaki ağaç onunla birlikte yeniden çiziliyor).
 */
export function t(tr: string, en: string): string {
  return current === 'en' ? en : tr;
}

export function getLanguage(): AppLanguage {
  return current;
}

/**
 * İngilizce sayı + isim: `plural(3, 'meeting', 'meetings')` → "3 meetings".
 * Türkçede sayıdan sonra isim çoğul olmadığı için yalnızca İngilizce tarafta
 * kullanılıyor: `t(`${n} toplantı`, plural(n, 'meeting', 'meetings'))`.
 */
export function plural(count: number, one: string, other: string): string {
  return `${count} ${count === 1 ? one : other}`;
}

/** `toLocaleDateString` / `toLocaleTimeString` için yerel ayar. */
export function dateLocale(): 'tr-TR' | 'en-US' {
  return current === 'en' ? 'en-US' : 'tr-TR';
}

interface LanguageContextValue {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(current);

  const apply = useCallback((next: AppLanguage) => {
    current = next;
    // CSS `uppercase` dile göre büyük harf yapıyor (i → İ yalnızca Türkçede).
    document.documentElement.lang = next;
    writeCachedLanguage(next);
    setLanguageState(next);
  }, []);

  // Önbellek, ana süreçteki kalıcı değerden farklıysa ona uy.
  useEffect(() => {
    let alive = true;
    // Promise.resolve: köprü hazır değilse senkron hata çizimi bozmasın.
    void Promise.resolve()
      .then(() => window.zabit.getAppLanguage())
      .then((stored) => {
        if (alive && stored !== current) apply(stored);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [apply]);

  const setLanguage = useCallback(
    async (next: AppLanguage) => {
      const previous = current;
      if (next === previous) return;
      apply(next);
      try {
        await window.zabit.setAppLanguage(next);
      } catch (err) {
        apply(previous);
        throw err;
      }
    },
    [apply]
  );

  const value = useMemo(() => ({ language, setLanguage }), [language, setLanguage]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/** Dili okuyan bileşen, dil değişince yeniden çizilir. */
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage, LanguageProvider dışında kullanıldı.');
  return { ...context, t, locale: dateLocale() };
}
